import { Suspense, lazy, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import SiteLayout from './components/ui/demo'
import HomePage from './pages/HomePage'
import ProtectedRoute from './components/ProtectedRoute'
import MaintenanceScreen from './components/ui/MaintenanceScreen'
import CookieBanner from './components/ui/CookieBanner'
import { useSiteSettings } from './lib/content'
import { trackPageView } from './lib/analytics'

const LiquidGradient = lazy(() => import('./components/ui/flow-gradient-hero-section'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const BlogsPage = lazy(() => import('./pages/BlogsPage'))
const WorksPage = lazy(() => import('./pages/WorksPage'))
const ExperiencePage = lazy(() => import('./pages/ExperiencePage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const ChatPopup = lazy(() => import('./components/ui/ChatPopup'))
const ExperienceDetailPage = lazy(() => import('./pages/ExperienceDetailPage'))
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'))
const BlogsListPage = lazy(() => import('./pages/BlogsListPage'))

// Admin Pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'))
const AdminLayout = lazy(() => import('./layouts/AdminLayout'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const ManageWorks = lazy(() => import('./pages/admin/ManageWorks'))
const ManageExperience = lazy(() => import('./pages/admin/ManageExperience'))
const ManageBlogs = lazy(() => import('./pages/admin/ManageBlogs'))
const ManageAbout = lazy(() => import('./pages/admin/ManageAbout'))
const AdminSettings = lazy(() => import('./pages/admin/Settings'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))

function MainLayout({ onChatOpen }: { onChatOpen: () => void }) {
  return (
    <>
      <Suspense fallback={null}><LiquidGradient asBackground /></Suspense>
      <div className="app-content-over-gradient">
        <SiteLayout onChatOpen={onChatOpen}>
          <section className="page-panel" data-page-id="home"><HomePage /></section>
          <section className="page-panel" data-page-id="about">
            <Suspense fallback={null}><AboutPage /></Suspense>
          </section>
          <section className="page-panel" data-page-id="experience">
            <Suspense fallback={null}><ExperiencePage /></Suspense>
          </section>
          <section className="page-panel" data-page-id="works">
            <Suspense fallback={null}><WorksPage /></Suspense>
          </section>
          <section className="page-panel" data-page-id="blogs">
            <Suspense fallback={null}><BlogsPage /></Suspense>
          </section>
          <section className="page-panel" data-page-id="contact">
            <Suspense fallback={null}><ContactPage /></Suspense>
          </section>
        </SiteLayout>
      </div>
    </>
  )
}

function App() {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const { settings: site } = useSiteSettings()
  const location = useLocation()

  // Site Configuration → document head. Blog/experience detail pages set their
  // own title on mount, so only apply this on routes that don't.
  useEffect(() => {
    if (!site) return
    const isDetailPage = /^\/(blog|experience)\//.test(location.pathname)
    if (isDetailPage) return

    if (site.site_title?.trim()) document.title = site.site_title.trim()
    if (site.site_description?.trim()) {
      const desc = site.site_description.trim()
      for (const sel of ['meta[name="description"]', 'meta[property="og:description"]']) {
        document.querySelector(sel)?.setAttribute('content', desc)
      }
    }
    if (site.site_title?.trim()) {
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', site.site_title.trim())
    }
  }, [site, location.pathname])

  // Admin area (and login) must stay reachable so maintenance can be turned off.
  const isAdminPath = location.pathname.startsWith('/admin')

  // Record public page views (no-op without consent; admin isn't tracked).
  useEffect(() => {
    if (isAdminPath) return
    trackPageView(location.pathname)
  }, [location.pathname, isAdminPath])

  if (site?.maintenance_mode && !isAdminPath) {
    return <MaintenanceScreen message={site.maintenance_message} />
  }

  return (
    <>
      <Routes>
        {/* The site is one scrolling page, but each section is also a real
            route so it can be linked, shared and indexed. SiteLayout scrolls to
            the matching panel on load. */}
        {['/', '/about', '/experience', '/works', '/contact'].map((path) => (
          <Route key={path} path={path} element={<MainLayout onChatOpen={() => setIsChatOpen(true)} />} />
        ))}
        <Route path="/experience/:slug" element={
          <Suspense fallback={null}><ExperienceDetailPage /></Suspense>
        } />
        <Route path="/blog/:slug" element={
          <Suspense fallback={null}><BlogDetailPage /></Suspense>
        } />
        <Route path="/blogs" element={
          <Suspense fallback={null}><BlogsListPage /></Suspense>
        } />
        <Route path="/privacy" element={
          <Suspense fallback={null}><PrivacyPolicy /></Suspense>
        } />

        {/* Admin Routes */}
        <Route path="/admin/login" element={
          <Suspense fallback={null}><AdminLogin /></Suspense>
        } />
        <Route element={<ProtectedRoute />}>
          <Route path="/admin" element={
            <Suspense fallback={null}><AdminLayout /></Suspense>
          }>
            <Route index element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
            <Route path="works" element={<Suspense fallback={null}><ManageWorks /></Suspense>} />
            <Route path="experience" element={<Suspense fallback={null}><ManageExperience /></Suspense>} />
            <Route path="blogs" element={<Suspense fallback={null}><ManageBlogs /></Suspense>} />
            <Route path="about" element={<Suspense fallback={null}><ManageAbout /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={null}><AdminSettings /></Suspense>} />
          </Route>
        </Route>

        {/* No match: the SPA rewrite serves index.html for every path, so
            without this a typo or dead link renders a blank page. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {isChatOpen && (
        <Suspense fallback={null}>
          <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
        </Suspense>
      )}

      {/* Consent gate — analytics stays off until this is accepted. */}
      {!isAdminPath && <CookieBanner />}
    </>
  )
}

export default App
